import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Edit, Trash, Plus } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { TourItem, TourFormData } from "@/types/database";

// Format number to IDR currency
function formatIDR(amount: number) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function Tours() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState<TourItem | null>(null);
  const [tourToDelete, setTourToDelete] = useState<TourItem | null>(null);
  const [sortField, setSortField] = useState<keyof TourItem>('tour_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState<TourFormData>({
    tour_name: "",
    tour_adult_price: 0,
    tour_child_price: 0,
    mpv_parking_price: 0,
    elf_short_parking_price: 0,
    hiace_parking_price: 0,
    elf_giga_parking_price: 0,
    bus_parking_price: 0,
  });

  const queryClient = useQueryClient();

  // Fetch tours
  const { data: tours = [], isLoading } = useQuery({
    queryKey: ["tours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      return data as TourItem[];
    },
  });

  // Add tour mutation
  const addTourMutation = useMutation({
    mutationFn: async (newTour: TourFormData) => {
      const { data, error } = await supabase
        .from("tours")
        .insert([newTour])
        .select()
        .single();

      if (error) throw error;
      return data as TourItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tours"] });
      toast.success("Tour added successfully");
      setIsAddDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add tour");
    },
  });

  // Edit tour mutation
  const editTourMutation = useMutation({
    mutationFn: async (updatedTour: TourFormData & { id: string }) => {
      const { id, ...tourData } = updatedTour;
      console.log("Updating tour with ID:", id);

      // First update the tour in the tours table
      const { error: tourError } = await supabase
        .from("tours")
        .update(tourData)
        .match({ id });

      if (tourError) {
        console.error("Update error:", tourError);
        throw tourError;
      }

      // Then update all matching tour references in tour_package table
      const { data: tourPackages, error: fetchError } = await supabase
        .from("tour_package")
        .select('id, selected_tour');

      if (fetchError) {
        console.error("Fetch error:", fetchError);
        throw fetchError;
      }

      // Update each tour package that references this tour
      for (const pkg of tourPackages) {
        try {
          const selectedTours = JSON.parse(pkg.selected_tour || '[]');
          let updated = false;

          // Update prices in the selected tours array
          const updatedTours = selectedTours.map((tour: any) => {
            if (tour.id === id) {
              updated = true;
              return {
                ...tour,
                tour_adult_price: tourData.tour_adult_price,
                tour_child_price: tourData.tour_child_price,
                tour_parking_price: tourData.tour_parking_price
              };
            }
            return tour;
          });

          // Only update if this package contained the modified tour
          if (updated) {
            const { error: updateError } = await supabase
              .from("tour_package")
              .update({ selected_tour: JSON.stringify(updatedTours) })
              .match({ id: pkg.id });

            if (updateError) {
              console.error("Package update error:", updateError);
              throw updateError;
            }
          }
        } catch (e) {
          console.error("Error processing tour package:", e);
          // Continue with other packages even if one fails
        }
      }

      return { id, ...tourData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tours"] });
      queryClient.invalidateQueries({ queryKey: ["tour_packages"] }); // Invalidate tour packages as well
      toast.success("Tour updated successfully!");
      setIsEditDialogOpen(false);
      setSelectedTour(null);
      resetForm();
    },
    onError: (error: Error) => {
      console.error("Mutation error:", error);
      toast.error(error.message);
      setIsEditDialogOpen(false);
      resetForm();
    },
  });

  // Delete tour mutation
  const deleteTourMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tours").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tours"] });
      toast.success("Tour deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete tour");
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof TourFormData) => {
    const value = e.target.value;
    if (field === 'tour_name') {
      setFormData({ ...formData, [field]: value });
    } else {
      const numValue = value === '' ? 0 : parseFloat(value);
      setFormData({ ...formData, [field]: isNaN(numValue) ? 0 : numValue });
    }
  };

  const resetForm = () => {
    setFormData({
      tour_name: "",
      tour_adult_price: 0,
      tour_child_price: 0,
      mpv_parking_price: 0,
      elf_short_parking_price: 0,
      hiace_parking_price: 0,
      elf_giga_parking_price: 0,
      bus_parking_price: 0,
    });
  };

  const handleSort = (field: keyof TourItem) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: keyof TourItem) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addTourMutation.mutate(formData);
  };

  const handleEdit = (tour: TourItem) => {
    if (!tour?.id) {
      console.error("Invalid tour:", tour);
      toast.error("Invalid tour selected");
      return;
    }

    console.log("Editing tour with ID:", tour.id);
    setSelectedTour(tour);
    setFormData({
      tour_name: tour.tour_name,
      tour_adult_price: tour.tour_adult_price,
      tour_child_price: tour.tour_child_price,
      mpv_parking_price: tour.mpv_parking_price || 0,
      elf_short_parking_price: tour.elf_short_parking_price || 0,
      hiace_parking_price: tour.hiace_parking_price || 0,
      elf_giga_parking_price: tour.elf_giga_parking_price || 0,
      bus_parking_price: tour.bus_parking_price || 0,
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTour?.id) {
      console.error("No valid tour selected");
      toast.error("No valid tour selected");
      return;
    }
    
    console.log("Submitting edit for tour:", selectedTour.id);
    // Keep the original tour_name from selectedTour to prevent changes to it
    editTourMutation.mutate({ 
      ...formData,
      tour_name: selectedTour.tour_name, // Use original tour name
      id: selectedTour.id 
    });
  };

  const handleDelete = async (tour: TourItem) => {
    setTourToDelete(tour);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (tourToDelete) {
      deleteTourMutation.mutate(tourToDelete.id);
      setIsDeleteDialogOpen(false);
      setTourToDelete(null);
    }
  };

  const SortableTableHead = ({ 
    field, 
    children 
  }: { 
    field: keyof TourItem, 
    children: React.ReactNode 
  }) => (
    <TableHead 
      className="min-w-[120px] cursor-pointer select-none transition-colors hover:bg-muted/50"
      onClick={() => handleSort(field)}
      role="columnheader"
      aria-sort={sortField === field ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSort(field);
        }
      }}
    >
      <div className="flex items-center">
        {children}
        {getSortIcon(field)}
      </div>
    </TableHead>
  );

  const filteredTours = tours.filter((tour) =>
    tour.tour_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedTours = [...filteredTours].sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    const fieldA = a[sortField];
    const fieldB = b[sortField];

    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
      return fieldA.localeCompare(fieldB) * multiplier;
    } else {
      return ((fieldA as number) - (fieldB as number)) * multiplier;
    }
  });

  return (
    <div className="flex flex-col gap-4 py-4 px-4">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Tours</h1>
          <p className="text-muted-foreground">Manage your tour packages here.</p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add New Tour
        </Button>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>All Tours</CardTitle>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search tours..."
                className="pl-9 w-full sm:w-[300px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-24">
              <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
            </div>
          ) : filteredTours.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
              {searchQuery ? (
                <>
                  <p>No tours found matching "{searchQuery}"</p>
                  <Button
                    variant="link"
                    className="mt-2"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear search
                  </Button>
                </>
              ) : (
                <>
                  <p>No tours added yet</p>
                  <Button
                    variant="link"
                    className="mt-2"
                    onClick={() => setIsAddDialogOpen(true)}
                  >
                    Add your first tour
                  </Button>
                </>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="h-4">
                  <TableHead className="w-[100px] text-xs p-2">ID</TableHead>
                  <SortableTableHead field="tour_name" className="text-xs p-2">Tour Name</SortableTableHead>
                  <SortableTableHead field="tour_adult_price" className="text-xs p-2">Adult Price</SortableTableHead>
                  <SortableTableHead field="tour_child_price" className="text-xs p-2">Child Price</SortableTableHead>
                  <SortableTableHead field="mpv_parking_price" className="text-xs p-2">MPV Parking Fee</SortableTableHead>
                  <TableHead className="w-[100px] text-right text-xs p-2">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTours.map((tour) => (
                  <TableRow key={tour.id} className="h-4 hover:bg-transparent border-gray-800">
                    <TableCell className="font-mono text-xs p-2">
                      {tour.id.substring(0, 8)}...
                    </TableCell>
                    <TableCell className="font-medium text-xs p-2">{tour.tour_name}</TableCell>
                    <TableCell className="tabular-nums text-xs text-right p-2">{formatIDR(tour.tour_adult_price)}</TableCell>
                    <TableCell className="tabular-nums text-xs text-right p-2">{formatIDR(tour.tour_child_price)}</TableCell>
                    <TableCell className="tabular-nums text-xs text-right p-2">{formatIDR(tour.mpv_parking_price)}</TableCell>
                    <TableCell className="text-right p-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(tour)}
                          className="hover:bg-muted"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(tour)}
                          className="hover:bg-muted"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {filteredTours.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground text-center">
              Showing {filteredTours.length} tour{filteredTours.length === 1 ? '' : 's'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Tour Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Tour</DialogTitle>
            <DialogDescription>
              Add a new tour package with pricing details. All prices should be in Indonesian Rupiah (IDR).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-6">
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="tour_name">Tour Name</Label>
                <Input
                  id="tour_name"
                  value={formData.tour_name}
                  onChange={(e) => handleInputChange(e, 'tour_name')}
                  placeholder="Enter tour name"
                  className="w-full"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Enter a descriptive name for the tour package
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="tour_adult_price" className="text-xs">Adult Price (IDR)</Label>
                  <Input
                    id="tour_adult_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.tour_adult_price.toString()}
                    onChange={(e) => handleInputChange(e, 'tour_adult_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="tour_child_price" className="text-xs">Child Price (IDR)</Label>
                  <Input
                    id="tour_child_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.tour_child_price.toString()}
                    onChange={(e) => handleInputChange(e, 'tour_child_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
              </div>
            </div>
            
            {/* Separator with title */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-500">Parking Information</span>
              </div>
            </div>
            
            {/* Parking Fees for different vehicle types */}
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="mpv_parking_price">MPV 1-6 Person</Label>
                  <Input
                    id="mpv_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.mpv_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'mpv_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="elf_short_parking_price">Elf Short 1-12 Person</Label>
                  <Input
                    id="elf_short_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.elf_short_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'elf_short_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="hiace_parking_price">Hiace 1-15 Person</Label>
                  <Input
                    id="hiace_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.hiace_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'hiace_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="elf_giga_parking_price">Elf Giga 1-19 Person</Label>
                  <Input
                    id="elf_giga_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.elf_giga_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'elf_giga_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bus_parking_price">Bus 30 Seater 1-30 Person</Label>
                  <Input
                    id="bus_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.bus_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'bus_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsAddDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addTourMutation.isPending}>
                {addTourMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 mr-2 rounded-full border-2 border-background border-t-transparent animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Tour'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Tour Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tour</DialogTitle>
            <DialogDescription>
              Update the tour package details and pricing. All prices should be in Indonesian Rupiah (IDR).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-6">
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="tour_name">Tour Name</Label>
                <div className="p-2 border rounded-md bg-gray-50 text-gray-700 w-full">
                  {formData.tour_name}
                </div>
                <p className="text-sm text-muted-foreground">
                  Tour name cannot be edited to maintain database integrity
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="tour_adult_price" className="text-xs">Adult Price (IDR)</Label>
                  <Input
                    id="tour_adult_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.tour_adult_price.toString()}
                    onChange={(e) => handleInputChange(e, 'tour_adult_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="tour_child_price" className="text-xs">Child Price (IDR)</Label>
                  <Input
                    id="tour_child_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.tour_child_price.toString()}
                    onChange={(e) => handleInputChange(e, 'tour_child_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
              </div>
            </div>
            
            {/* Separator with title */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-500">Parking Information</span>
              </div>
            </div>
            
            {/* Parking Fees for different vehicle types */}
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="mpv_parking_price">MPV 1-6 Person</Label>
                  <Input
                    id="mpv_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.mpv_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'mpv_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="elf_short_parking_price">Elf Short 1-12 Person</Label>
                  <Input
                    id="elf_short_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.elf_short_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'elf_short_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="hiace_parking_price">Hiace 1-15 Person</Label>
                  <Input
                    id="hiace_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.hiace_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'hiace_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="elf_giga_parking_price">Elf Giga 1-19 Person</Label>
                  <Input
                    id="elf_giga_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.elf_giga_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'elf_giga_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bus_parking_price">Bus 30 Seater 1-30 Person</Label>
                  <Input
                    id="bus_parking_price"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.bus_parking_price.toString()}
                    onChange={(e) => handleInputChange(e, 'bus_parking_price')}
                    placeholder="0"
                    className="w-full h-8 text-xs"
                    required
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsEditDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={editTourMutation.isPending}>
                {editTourMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 mr-2 rounded-full border-2 border-background border-t-transparent animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tour</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{tourToDelete?.tour_name}"?<br />
              This action cannot be undone and will permanently remove the tour package.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setTourToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTourMutation.isPending}
            >
              {deleteTourMutation.isPending ? (
                <>
                  <div className="h-4 w-4 mr-2 rounded-full border-2 border-background border-t-transparent animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Tour'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}